import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { useWebsocketConnection } from 'providers/WebsocketProvider'
import { ArticleMeta } from 'types'
import { articleMetaUpsert } from 'redux/actions'
import { useQueryClient } from 'react-query'
import { layersKeys } from './queries/layers/layersKeys'
import { trackedChangesKeys } from './queries/trackedChanges/trackedChangesKeys'
import { informalFeedbackRequestsKeys } from './queries/informalFeedbackRequests/informalFeedbackRequestsKeys'
import { useArticlePassword } from './useArticlePassword'
import { useSyncArticleSlug } from './useSyncArticleSlug'

interface Props {
  id: string
}

enum Operation {
  Invalidate = 'invalidate',
}

enum EntityType {
  LAYERS = 'layers',
  TRACKED_CHANGES = 'tracked_changes',
  INFORMAL_FEEDBACK_REQUESTS = 'informal_feedback_requests',
}

type ReceivedPayload = OperationPayload & {
  article?: ArticleMeta
}

type OperationPayload = InvalidatePayload | null

interface InvalidatePayload {
  op: Operation.Invalidate
  entities: Array<InvalidatedEntity>
}

type InvalidatedLayer = {
  type: EntityType.LAYERS
}

type InvalidatedTrackedChanges = {
  type: EntityType.TRACKED_CHANGES
  article_id: string
  layer_id: string
}

type InvalidatedInformalFeedbackRequest = {
  type: EntityType.INFORMAL_FEEDBACK_REQUESTS
  article_id: string
  layer_id: string
}

type InvalidatedEntity = InvalidatedLayer | InvalidatedTrackedChanges | InvalidatedInformalFeedbackRequest

export const useArticleSubscription = ({ id }: Props) => {
  const websocketConnection = useWebsocketConnection()
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const password = useArticlePassword(id)
  const syncArticleSlug = useSyncArticleSlug(id)

  useEffect(() => {
    if (!websocketConnection) return

    const subscription = websocketConnection.subscriptions.create(
      { channel: 'ArticleChannel', article_id: id },
      {
        received: ({ article, op, entities }: ReceivedPayload) => {
          if (article) {
            syncArticleSlug(article.slug)
            dispatch(articleMetaUpsert(article))
          }
          switch (op) {
            case Operation.Invalidate:
              entities.forEach((entity) => {
                if (entity.type === EntityType.LAYERS) {
                  queryClient.invalidateQueries(layersKeys.list(id, { password }))
                  queryClient.invalidateQueries([layersKeys.prefix, 'detail'])
                  return
                }

                if (entity.type === EntityType.TRACKED_CHANGES) {
                  queryClient.invalidateQueries(trackedChangesKeys.list(entity.layer_id, { password }))
                  return
                }

                if (entity.type === EntityType.INFORMAL_FEEDBACK_REQUESTS) {
                  queryClient.invalidateQueries([informalFeedbackRequestsKeys.prefix, entity.article_id])
                  return
                }
              })
              break
            default:
          }
        },
      },
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [id, dispatch, websocketConnection, password, queryClient, syncArticleSlug])
}
